// deps
import React, { memo, useCallback, useMemo } from "react";

// interfaces
import { CardPaymentMethodData, ICardPaymentGroup, CardPaymentGroupOptions } from "@payment-ui/interfaces/Card";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// dls
import AddFilledIcon from "@swiggy-private/dls-web-icons/dist/web/generic/addFilled/12";

// widgets
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import Alert from "@payment-ui/widgets/alert/Alert";
import { Message, MESSAGE_TYPES } from "@payment-ui/widgets/message";

// components
import InfoIcon from "@payment-ui/icons/infoIcon";

// helpers
import { canEnablePaymentMethod, getPaymentMethodPromoMsg } from "@payment-ui/helpers/Payment";
import { getNewCardMethodTestIds } from "@payment-ui/helpers/Card";

// styles
import {
    PaymentMethod,
    PaymentMethodContainer,
    IconContainer,
    Icon,
    AlertMessage,
    NonTokenizeCardRemovalContainer,
    NonTokenizeCardRemovalMsg,
} from "./styles/NewCard.styled";

export interface IProps extends PaymentMethodProps {
    key: string;
    methodData: CardPaymentMethodData;
    paymentActions: ICardPaymentGroup["paymentActions"];
    options: CardPaymentGroupOptions;
    nonTokenizeCardRemovalMsg?: string;
}

const INFO_ICON_SIZE = 16;

function NewCardPaymentMethod(props: IProps): React.ReactElement {
    const promoMsg = getPaymentMethodPromoMsg(props.methodData);
    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled), [
        props.isPaymentGroupEnabled,
        props.methodData,
    ]);
    const { onSelect } = props.paymentActions;
    const { displayName } = props.methodData;

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            isMethodEnabled && onSelect(props.methodData);
        },
        [onSelect, props.methodData, isMethodEnabled],
    );

    const testIds = useMemo(() => getNewCardMethodTestIds(props.methodData), [props.methodData]);

    return (
        <PaymentMethod $isDisabled={!isMethodEnabled} onClick={onClick} data-testid={testIds.CONTAINER}>
            <PaymentMethodContainer>
                <IconContainer>
                    <Icon
                        $isDisabled={!isMethodEnabled}
                        data-testid={testIds.ICON}
                        Icon={AddFilledIcon}
                        fillColor="primary"
                    />
                </IconContainer>

                <PaymentMethodName
                    displayName={displayName}
                    promoMsg={promoMsg}
                    enabled={isMethodEnabled}
                    data-testid={testIds.NAME}
                    color="primary"
                >
                    {!isMethodEnabled && (
                        <AlertMessage>
                            <Alert
                                message={props.methodData.meta.disableMessage}
                                data-testid={testIds.DISABLE_MESSAGE}
                            />
                        </AlertMessage>
                    )}
                </PaymentMethodName>
            </PaymentMethodContainer>

            {props.nonTokenizeCardRemovalMsg && (
                <NonTokenizeCardRemovalContainer data-testid={testIds.NON_TOKENIZE_CARD_REMOVAL_MSG}>
                    <InfoIcon size={INFO_ICON_SIZE} />
                    <NonTokenizeCardRemovalMsg>
                        <Message message={props.nonTokenizeCardRemovalMsg} type={MESSAGE_TYPES.INFO} />
                    </NonTokenizeCardRemovalMsg>
                </NonTokenizeCardRemovalContainer>
            )}
        </PaymentMethod>
    );
}

export default memo(NewCardPaymentMethod);
